import { useCallback, useEffect, useRef, useState } from "react";
import {
  BUBBLE_ASPECT_RATIO,
  BUBBLE_MAX_HEIGHT_RATIO,
  BUBBLE_MIN_HEIGHT_DESKTOP,
  BUBBLE_MIN_HEIGHT_MOBILE,
  BUBBLE_RESIZE_MIN_HEIGHT_DESKTOP,
  BUBBLE_RESIZE_MIN_HEIGHT_MOBILE,
} from "@/components/session/bubble";
import { useIsMobile } from "./use-mobile";

interface BubbleSize {
  width: number;
  height: number;
}

interface ResizeDragState {
  startY: number;
  startHeight: number;
  pointerId: number;
}

export function useBubbleSize() {
  const [container, setContainer] = useState<HTMLElement | null>(null);
  const [containerHeight, setContainerHeight] = useState(0);
  const [userHeight, setUserHeight] = useState<number | null>(null);
  const resizeStateRef = useRef<ResizeDragState | null>(null);
  const isMobile = useIsMobile();

  const containerRef = useCallback((node: HTMLElement | null) => {
    setContainer(node);
  }, []);

  const layoutMin = isMobile ? BUBBLE_MIN_HEIGHT_MOBILE : BUBBLE_MIN_HEIGHT_DESKTOP;
  const resizeMin = isMobile
    ? BUBBLE_RESIZE_MIN_HEIGHT_MOBILE
    : BUBBLE_RESIZE_MIN_HEIGHT_DESKTOP;
  const maxHeight = Math.max(containerHeight * BUBBLE_MAX_HEIGHT_RATIO, resizeMin);

  useEffect(() => {
    if (!container) return;

    const ro = new ResizeObserver((entries) => {
      for (const entry of entries) {
        setContainerHeight(entry.contentRect.height);
      }
    });

    ro.observe(container);
    setContainerHeight(container.getBoundingClientRect().height);

    return () => ro.disconnect();
  }, [container]);

  // Clamp a user-chosen height when the container changes so the bubble never
  // disappears or explodes outside the viewport.
  useEffect(() => {
    if (userHeight === null) return;
    const clamped = Math.min(Math.max(userHeight, resizeMin), maxHeight);
    if (clamped !== userHeight) {
      setUserHeight(clamped);
    }
  }, [containerHeight, resizeMin, maxHeight, userHeight]);

  const defaultHeight = Math.max(containerHeight / 3, layoutMin);
  const activeMin = userHeight !== null ? resizeMin : layoutMin;
  const height = Math.min(Math.max(userHeight ?? defaultHeight, activeMin), maxHeight);
  const width = Math.round(height * BUBBLE_ASPECT_RATIO);

  const onResizeStart = useCallback(
    (e: React.PointerEvent<HTMLElement>) => {
      e.stopPropagation();
      resizeStateRef.current = {
        startY: e.clientY,
        startHeight: height,
        pointerId: e.pointerId,
      };
      e.currentTarget.setPointerCapture(e.pointerId);
    },
    [height],
  );

  const onResizeMove = useCallback(
    (e: React.PointerEvent<HTMLElement>) => {
      const state = resizeStateRef.current;
      if (!state) return;
      const nextHeight = Math.min(
        Math.max(state.startHeight + (e.clientY - state.startY), resizeMin),
        maxHeight,
      );
      setUserHeight(nextHeight);
    },
    [resizeMin, maxHeight],
  );

  const onResizeEnd = useCallback((e: React.PointerEvent<HTMLElement>) => {
    const state = resizeStateRef.current;
    if (state) {
      try {
        e.currentTarget.releasePointerCapture(state.pointerId);
      } catch {
        // ignore
      }
    }
    resizeStateRef.current = null;
  }, []);

  return {
    containerRef,
    size: (container ? { width, height } : { width: 0, height: 0 }) as BubbleSize,
    isResized: userHeight !== null,
    onResizeStart,
    onResizeMove,
    onResizeEnd,
  };
}
